import { Store } from '@tanstack/react-store';
import type {
  Message,
  Thread,
  ChatSettings,
  UserProfile,
  SidebarTab,
  ThreadCategory,
} from '@/types/ai-chat';


const seedThreads: Thread[] = [
  {
    id: 'thread-1',
    title: 'eTIMS Setup for Restaurant Chain',
    preview: 'I need help setting up eTIMS for my 5-location restaurant business…',
    createdAt: new Date('2024-03-15T10:30:00'),
    updatedAt: new Date('2024-03-15T11:45:00'),
    messageCount: 12,
    pinned: true,
    category: 'etims',
    tags: ['eTIMS', 'VAT', 'Setup'],
  },
  {
    id: 'thread-2',
    title: 'KRA Audit Notice — Urgent',
    preview: 'I just received an audit notice from KRA for FY 2022/2023…',
    createdAt: new Date('2024-03-14T08:00:00'),
    updatedAt: new Date('2024-03-14T09:30:00'),
    messageCount: 8,
    pinned: true,
    category: 'audit',
    tags: ['KRA Audit', 'Urgent'],
  },
  {
    id: 'thread-3',
    title: 'Corporate Tax Returns FY 2023',
    preview: 'What are the deadlines for corporate income tax returns?',
    createdAt: new Date('2024-03-12T14:00:00'),
    updatedAt: new Date('2024-03-12T14:45:00'),
    messageCount: 5,
    pinned: false,
    category: 'tax-returns',
    tags: ['Corporate Tax', 'Deadlines'],
  },
  {
    id: 'thread-4',
    title: 'Transfer Pricing Documentation',
    preview: 'Our parent company in the UK requires arm length pricing…',
    createdAt: new Date('2024-03-10T11:00:00'),
    updatedAt: new Date('2024-03-10T12:15:00'),
    messageCount: 15,
    pinned: false,
    category: 'international',
    tags: ['Transfer Pricing', 'UK', 'International'],
  },
  {
    id: 'thread-5',
    title: 'Monthly Payroll PAYE Computation',
    preview: 'How do I compute PAYE for an employee earning KES 150,000?',
    createdAt: new Date('2024-03-08T09:00:00'),
    updatedAt: new Date('2024-03-08T09:30:00'),
    messageCount: 4,
    pinned: false,
    category: 'bookkeeping',
    tags: ['PAYE', 'Payroll'],
  },
  {
    id: 'thread-6',
    title: 'VAT Registration Requirements',
    preview: 'My turnover just exceeded KES 5 million. Do I need to register for VAT?',
    createdAt: new Date('2024-03-05T15:00:00'),
    updatedAt: new Date('2024-03-05T15:20:00'),
    messageCount: 3,
    pinned: false,
    category: 'kra-registration',
    tags: ['VAT', 'Registration'],
  },
];

const welcomeMessage: Message = {
  id: 'welcome-msg',
  role: 'assistant',
  content: `Welcome to **Rosewood AI** — your intelligent tax compliance assistant powered by Kenya's leading tax experts.

I can help you with:
- **KRA compliance** — registration, returns, eTIMS setup
- **Tax planning** — minimising your tax burden legally
- **Audit defence** — navigating KRA audit notices
- **Payroll & bookkeeping** — PAYE, NHIF, NSSF computations
- **International tax** — transfer pricing, double tax treaties

Ask me anything about Kenyan tax law, or choose a topic below to get started. For complex matters, I'll recommend connecting you with one of our certified CPAs.`,
  status: 'complete',
  timestamp: new Date(),
  threadId: 'new',
};


interface ChatState {
  threads: Thread[];
  activeThreadId: string | null;
  messages: Record<string, Message[]>;

  sidebarOpen: boolean;
  sidebarTab: SidebarTab;
  sidebarSearchQuery: string;
  isStreaming: boolean;
  inputValue: string;
  attachmentPreviews: File[];

  settings: ChatSettings;
  user: UserProfile;

  threadFilter: ThreadCategory | 'all';
  threadSearch: string;
}

const defaultSettings: ChatSettings = {
  model: 'rosewood-expert-v2',
  temperature: 0.3,
  maxTokens: 2048,
  streamingEnabled: true,
  citationsEnabled: true,
  memoryEnabled: true,
  language: 'en',
};

const defaultUser: UserProfile = {
  id: 'user-1',
  name: 'John Kamau',
  email: 'john@businesske.co.ke',
  company: 'BizKenya Ltd',
  role: 'Finance Manager',
  avatarInitials: 'JK',
  plan: 'pro',
  messagesUsed: 47,
  messagesLimit: 200,
};

export const chatStore = new Store<ChatState>({
  threads: seedThreads,
  activeThreadId: null,
  messages: { 'new': [welcomeMessage] },
  sidebarOpen: true,
  sidebarTab: 'threads',
  sidebarSearchQuery: '',
  isStreaming: false,
  inputValue: '',
  attachmentPreviews: [],
  settings: defaultSettings,
  user: defaultUser,
  threadFilter: 'all',
  threadSearch: '',
});


export const chatActions = {
  setActiveThread: (threadId: string | null) => {
    chatStore.setState(s => ({ ...s, activeThreadId: threadId }));
  },

  startNewChat: () => {
    chatStore.setState(s => ({
      ...s,
      activeThreadId: null,
      messages: {
        ...s.messages,
        'new': [welcomeMessage],
      },
    }));
  },

  setSidebarOpen: (open: boolean) => {
    chatStore.setState(s => ({ ...s, sidebarOpen: open }));
  },

  toggleSidebar: () => {
    chatStore.setState(s => ({ ...s, sidebarOpen: !s.sidebarOpen }));
  },

  setSidebarTab: (tab: SidebarTab) => {
    chatStore.setState(s => ({ ...s, sidebarTab: tab }));
  },

  setInputValue: (value: string) => {
    chatStore.setState(s => ({ ...s, inputValue: value }));
  },

  setThreadFilter: (filter: ThreadCategory | 'all') => {
    chatStore.setState(s => ({ ...s, threadFilter: filter }));
  },

  setThreadSearch: (query: string) => {
    chatStore.setState(s => ({ ...s, threadSearch: query }));
  },

  pinThread: (threadId: string) => {
    chatStore.setState(s => ({
      ...s,
      threads: s.threads.map(t =>
        t.id === threadId ? { ...t, pinned: !t.pinned } : t
      ),
    }));
  },

  deleteThread: (threadId: string) => {
    chatStore.setState(s => ({
      ...s,
      threads: s.threads.filter(t => t.id !== threadId),
      activeThreadId: s.activeThreadId === threadId ? null : s.activeThreadId,
    }));
  },

  sendMessage: (content: string) => {
    const threadId = chatStore.state.activeThreadId ?? 'new';
    const userMsg: Message = {
      id: `msg-${Date.now()}-user`,
      role: 'user',
      content,
      status: 'complete',
      timestamp: new Date(),
      threadId,
    };

    const typingMsg: Message = {
      id: `msg-${Date.now()}-typing`,
      role: 'assistant',
      content: '',
      status: 'streaming',
      timestamp: new Date(),
      threadId,
      isTyping: true,
    };

    chatStore.setState(s => ({
      ...s,
      inputValue: '',
      isStreaming: true,
      messages: {
        ...s.messages,
        [threadId]: [...(s.messages[threadId] ?? []), userMsg, typingMsg],
      },
    }));

    // Simulate streaming response
    const responses = getAIResponse(content);
    let charIndex = 0;
    const typingId = typingMsg.id;

    const streamInterval = setInterval(() => {
      charIndex = Math.min(charIndex + Math.floor(Math.random() * 8) + 4, responses.length);
      const partial = responses.slice(0, charIndex);

      chatStore.setState(s => ({
        ...s,
        messages: {
          ...s.messages,
          [threadId]: s.messages[threadId].map(m =>
            m.id === typingId
              ? { ...m, content: partial, isTyping: charIndex < responses.length }
              : m
          ),
        },
      }));

      if (charIndex >= responses.length) {
        clearInterval(streamInterval);

        chatStore.setState(s => {
          const isNew = threadId === 'new' && !s.activeThreadId;
          const newThreadId = isNew ? `thread-${Date.now()}` : threadId;
          const newThread: Thread | null = isNew
            ? {
                id: newThreadId,
                title: content.slice(0, 48) + (content.length > 48 ? '…' : ''),
                preview: content,
                createdAt: new Date(),
                updatedAt: new Date(),
                messageCount: 2,
                pinned: false,
                category: 'general',
                tags: [],
              }
            : null;

          const updatedMessages = { ...s.messages };
          if (isNew) {
            updatedMessages[newThreadId] = updatedMessages['new'].map(m =>
              m.id === typingId
                ? { ...m, status: 'complete' as const, isTyping: false, threadId: newThreadId }
                : { ...m, threadId: newThreadId }
            );
            updatedMessages['new'] = [welcomeMessage];
          } else {
            updatedMessages[threadId] = updatedMessages[threadId].map(m =>
              m.id === typingId
                ? { ...m, status: 'complete' as const, isTyping: false }
                : m
            );
          }

          return {
            ...s,
            isStreaming: false,
            activeThreadId: isNew ? newThreadId : s.activeThreadId,
            threads: newThread
              ? [newThread, ...s.threads]
              : s.threads.map(t =>
                  t.id === threadId
                    ? { ...t, updatedAt: new Date(), messageCount: t.messageCount + 2 }
                    : t
                ),
            messages: updatedMessages,
          };
        });
      }
    }, 28);
  },

  setMessageFeedback: (threadId: string, messageId: string, feedback: 'positive' | 'negative') => {
    chatStore.setState(s => ({
      ...s,
      messages: {
        ...s.messages,
        [threadId]: s.messages[threadId].map(m =>
          m.id === messageId ? { ...m, feedback } : m
        ),
      },
    }));
  },

  updateSettings: (partial: Partial<ChatSettings>) => {
    chatStore.setState(s => ({
      ...s,
      settings: { ...s.settings, ...partial },
    }));
  },
};

function getAIResponse(input: string): string {
  const lower = input.toLowerCase();

  if (lower.includes('etims') || lower.includes('electronic tax invoice')) {
    return `**eTIMS (Electronic Tax Invoice Management System)** is now mandatory for all VAT-registered taxpayers in Kenya.

Here's what you need to know:

**Who must comply?**
- All VAT-registered businesses (mandatory)
- Non-VAT businesses with annual turnover above KES 5 million

**Available solutions:**
1. **VSCU** (Virtual Sales Control Unit) — for businesses with existing POS/ERP systems
2. **OSCU** (Online Sales Control Unit) — standalone device
3. **eTIMS Lite** — web-based, for low-volume businesses

**Penalties for non-compliance:**
- Fines up to **KES 1,000,000**
- Risk of business suspension by KRA

**Next steps I recommend:**
1. Log in to your KRA iTax portal
2. Navigate to eTIMS registration
3. Select the solution type that fits your operations

Would you like me to walk you through the registration process step-by-step, or do you need help selecting the right eTIMS solution for your business type? I can also connect you with our eTIMS specialist **Faith Kamau** for hands-on setup support.`;
  }

  if (lower.includes('kra pin') || lower.includes('registration') || lower.includes('register')) {
    return `**KRA PIN Registration** is the first step to tax compliance in Kenya. Here's a complete guide:

**Documents Required:**

*For Individuals:*
- National ID or Passport (copy)
- Passport-size photograph
- Physical address details

*For Companies:*
- Certificate of Incorporation
- Memorandum & Articles of Association
- Directors' National IDs/Passports
- KRA PINs of all directors
- CR12 (Certificate of Official Search)

**Registration Process:**
1. Visit [itax.kra.go.ke](https://itax.kra.go.ke)
2. Click **"New PIN Registration"**
3. Select entity type (Individual / Non-Individual)
4. Fill in all required details accurately
5. Upload supporting documents
6. Submit and await PIN certificate via email

**Typical Processing Time:**
- Individuals: **1–3 business days**
- Companies: **3–7 business days**

>  **Important:** Errors in the iTax application can cause significant delays. Our team handles this for you with zero errors guaranteed.

Would you like Rosewood to handle your registration? We can have your PIN processed within 24 hours in most cases.`;
  }

  if (lower.includes('audit') || lower.includes('kra notice')) {
    return `Receiving a **KRA audit notice** can feel alarming — but with the right representation, most audits are resolved successfully.

**Immediate steps to take:**

1. **Don't respond to KRA directly yet** — contact a tax advisor first
2. **Note the deadline** — KRA notices typically give 14–30 days
3. **Gather your records** — bank statements, returns, invoices for the period

**What KRA is likely looking for:**
- Discrepancies between filed returns and third-party data
- Unexplained income or deductions
- VAT input/output mismatches
- Payroll inconsistencies

**Your rights as a taxpayer:**
- Right to professional representation
- Right to request additional time to respond
- Right to object to any assessment within **30 days**
- Right to appeal at the **Tax Appeals Tribunal**

**Rosewood's audit defence track record:**
- **97%** success rate in KRA audit cases
- Average **60%** reduction in proposed assessments
- Penalty waivers secured in majority of cases

>  **This sounds urgent.** I strongly recommend speaking with our Audit Defence Manager **Michael Njoroge** today.

Would you like me to help you prepare an initial response, or shall I flag this for immediate escalation to our audit team?`;
  }

  if (lower.includes('paye') || lower.includes('payroll') || lower.includes('salary')) {
    return `**PAYE (Pay As You Earn)** in Kenya is computed using progressive tax bands. Here's the 2024 computation:

**Tax Bands (2024):**

| Monthly Income (KES) | Rate |
|---|---|
| 0 – 24,000 | 10% |
| 24,001 – 32,333 | 25% |
| 32,334 – 500,000 | 30% |
| 500,001 – 800,000 | 32.5% |
| Above 800,000 | 35% |

**Standard Reliefs:**
- Personal Relief: **KES 2,400/month**
- Insurance Relief: 15% of premiums paid (max KES 5,000/month)

**Example — Employee earning KES 150,000/month:**
\`\`\`
First KES 24,000 × 10%     = KES 2,400
Next KES 8,333 × 25%       = KES 2,083
Remaining KES 117,667 × 30% = KES 35,300
Gross Tax                  = KES 39,783
Less: Personal Relief      = (KES 2,400)
Net PAYE                   = KES 37,383
\`\`\`

**Also deduct:**
- NHIF: **KES 1,700/month** (employee contribution)
- NSSF: **KES 2,160/month** (Tier I + Tier II combined)
- NITA Levy: **0.5% of gross** (employer obligation)

Would you like a full payroll computation for your team, or help setting up automated payroll processing?`;
  }

  if (lower.includes('vat') || lower.includes('value added tax')) {
    return `**VAT (Value Added Tax)** in Kenya is charged at **16%** on standard-rated supplies.

**Key VAT facts:**
- Standard rate: **16%**
- Zero-rated supplies: Exports, certain foods, medical supplies
- Exempt supplies: Financial services, land, educational fees
- Registration threshold: Annual turnover of **KES 5 million**

**VAT Return deadlines:**
- Filed **monthly** — due by the **20th of the following month**
- Late filing penalty: **5% of tax due** per month
- Electronic filing via **iTax** is mandatory

**Input VAT claims:**
You can claim input VAT on purchases made for your taxable business activities. From March 2024, all input VAT claims must be supported by **eTIMS-validated invoices**.

**Common VAT mistakes to avoid:**
1. Claiming VAT on exempt or non-business expenditure
2. Not reconciling input/output VAT monthly
3. Missing the 20th deadline
4. Invalid eTIMS invoices from suppliers

Is there a specific VAT question — such as whether your supplies are taxable, how to handle mixed supplies, or how to claim a VAT refund?`;
  }

  return `Thank you for your question. Based on Kenyan tax law and **KRA regulations**, here's my guidance:

This is an important tax compliance matter that requires careful consideration of:

**Relevant legislation:**
- Income Tax Act (Cap 470)
- Value Added Tax Act, 2013
- Tax Procedures Act, 2015
- KRA administrative guidelines

**Key considerations for your situation:**
1. **Filing obligations** — Ensure all applicable returns are filed on time to avoid the 5% per month late penalty
2. **Documentation** — Maintain proper records for at least 5 years (KRA's standard audit window)
3. **Compliance calendar** — Set reminders for all KRA deadlines relevant to your business type

**My recommendation:**
For matters of this complexity, I recommend scheduling a consultation with one of Rosewood's certified tax advisors who can review your specific circumstances and provide personalised advice.

> 💡 **Did you know?** Rosewood clients save an average of **10–30%** on their effective tax rate through legal tax planning strategies.

Is there a specific aspect of this I can explain further, or would you like me to help you prepare for a consultation with our team?`;
}